import type { Column } from "@judo/model-api";
import type { GridFilterModel } from "@mui/x-data-grid";
import { getColumnType } from "./table-column-utils";

/**
 * Map MUI DataGrid filter operators to JUDO REST API operators.
 *
 * MUI operators: https://mui.com/x/api/data-grid/grid-filter-operator/
 * JUDO operators: See REST_API_SPECIFICATION.md
 */
const OPERATOR_MAP: Record<string, string> = {
	// String operators
	contains: "like",
	equals: "equal",
	startsWith: "like",
	endsWith: "like",
	isEmpty: "isEmpty",
	isNotEmpty: "isNotEmpty",
	isAnyOf: "equal", // Will be handled specially for multiple values
	// Comparison operators (used for strings, numbers, and dates)
	"=": "equal",
	"!=": "notEqual",
	">": "greaterThan",
	">=": "greaterOrEqual",
	"<": "lessThan",
	"<=": "lessOrEqual",
	// Date operators (MUI DataGrid date filter operators)
	is: "equal",
	not: "notEqual",
	after: "greaterThan",
	onOrAfter: "greaterOrEqual",
	before: "lessThan",
	onOrBefore: "lessOrEqual",
};

/**
 * Convert MUI DataGrid filter value to JUDO REST API filter value.
 * Handles SQL LIKE patterns for string operators and date formatting.
 *
 * @param operator - The filter operator
 * @param value - The filter value
 * @param columnType - The DataGrid column type (from getColumnType)
 */
function convertFilterValue(operator: string, value: any, columnType?: string): any {
	if (value === null || value === undefined) {
		return value;
	}

	// Handle Date values for date columns (strip time portion)
	if (columnType === "date" && value instanceof Date) {
		// Return YYYY-MM-DD format (strip time portion)
		const year = value.getFullYear();
		const month = String(value.getMonth() + 1).padStart(2, "0");
		const day = String(value.getDate()).padStart(2, "0");
		return `${year}-${month}-${day}`;
	}

	// Handle Date values for dateTime columns (keep full ISO string)
	if (columnType === "dateTime" && value instanceof Date) {
		return value.toISOString();
	}

	// Convert contains/startsWith/endsWith to SQL LIKE patterns
	if (operator === "contains") {
		return `%${value}%`;
	}
	if (operator === "startsWith") {
		return `${value}%`;
	}
	if (operator === "endsWith") {
		return `%${value}`;
	}

	return value;
}

/**
 * Convert MUI GridFilterModel to QueryCustomizer filter format.
 *
 * MUI format: { items: [{ field: "name", operator: "contains", value: "John" }] }
 * JUDO format: { "name": [{ operator: "like", value: "%John%" }] }
 *
 * @param model - MUI DataGrid filter model
 * @param columns - Table columns for type information (optional)
 * @returns QueryCustomizer-compatible filter object
 */
export function convertFilterModelToQueryCustomizer(
	model: GridFilterModel,
	columns?: Column[]
): Record<string, any> | undefined {
	if (!model.items || model.items.length === 0) {
		return undefined;
	}

	// Build a map of field names to column types for efficient lookup
	const columnTypeMap = new Map<string, string>();
	if (columns) {
		for (const column of columns) {
			const fieldName = column.attributeType?.name ?? column.name;
			const columnType = getColumnType(column);
			columnTypeMap.set(fieldName, columnType);
		}
	}

	const filters: Record<string, any[]> = {};

	for (const item of model.items) {
		// Skip items without field or operator
		if (!item.field || !item.operator) continue;

		// Skip items without a value (unless it's isEmpty/isNotEmpty which don't require a value)
		const isEmptyOperator = item.operator === "isEmpty" || item.operator === "isNotEmpty";
		if (!isEmptyOperator && (item.value === undefined || item.value === null || item.value === "")) {
			continue;
		}

		// Map MUI operator to JUDO operator
		const judoOperator = OPERATOR_MAP[item.operator] || item.operator;

		// Get column type for this field
		const columnType = columnTypeMap.get(item.field);

		// Convert value (add LIKE wildcards if needed, strip time for dates)
		const filterValue = convertFilterValue(item.operator, item.value, columnType);

		// Initialize array for this field if not exists
		if (!filters[item.field]) {
			filters[item.field] = [];
		}

		// Add filter condition
		filters[item.field].push({
			operator: judoOperator,
			value: filterValue,
		});
	}

	return Object.keys(filters).length > 0 ? filters : undefined;
}
